// app/javascript/pages/Student/Badges/Index.tsx
import { useState } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  Medal, Lock, CheckCircle2, Trophy, Target, Search, FileText, Coins, Flame,
  BookOpen, Swords, Bookmark, Brain, Award, Crown, Zap, BookmarkCheck,
  Crosshair, ShieldCheck, Shield, Sparkles, GraduationCap
} from 'lucide-react'
import type { BadgeData } from '@/types'

const BADGE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  // Bronze
  first_test: Target,
  question_explorer: Search,
  test_taker: FileText,
  bookworm: Bookmark,
  streak_3: Flame,
  credit_earner: Coins,

  // Silver
  streak_7: Flame,
  streak_14: Zap,
  consistent_learner: BookOpen,
  mock_warrior: Swords,
  revision_master: BookmarkCheck,
  accuracy_ace: Crosshair,

  // Gold
  streak_30: ShieldCheck,
  streak_60: Sparkles,
  question_master: Brain,
  knowledge_titan: GraduationCap,
  exam_champion: Trophy,
  test_titan: Shield,
  sharpshooter: Crosshair,
  top_ranker: Medal,
  apex_achiever: Crown,
}

interface Props {
  badges: BadgeData[]
  stats: { earned_count: number; total_count: number }
}

const TIER_CONFIG = {
  bronze: {
    label: 'Bronze Achievements',
    icon: Award,
    iconColor: 'text-tertiary',
    badgeClass: 'bg-tertiary/10 text-tertiary border-tertiary/20',
    earnedBg: 'bg-card border-tertiary/40 shadow-glass-sm',
  },
  silver: {
    label: 'Silver Achievements',
    icon: Medal,
    iconColor: 'text-muted-foreground',
    badgeClass: 'bg-muted text-muted-foreground border-border',
    earnedBg: 'bg-card border-border shadow-glass-sm',
  },
  gold: {
    label: 'Gold Achievements',
    icon: Trophy,
    iconColor: 'text-primary',
    badgeClass: 'bg-primary/10 text-primary border-primary/20',
    earnedBg: 'bg-card border-primary/40 shadow-glass-sm',
  }
}

export default function BadgesIndex({ badges, stats }: Props) {
  const [activeFilter, setActiveFilter] = useState<'all' | 'earned' | 'locked'>('all')

  const overallPct = Math.round((stats.earned_count / Math.max(stats.total_count, 1)) * 100)

  const filteredBadges = badges.filter(b => {
    if (activeFilter === 'earned') return b.earned
    if (activeFilter === 'locked') return !b.earned
    return true
  })

  const groupedByTier = {
    bronze: filteredBadges.filter(b => b.tier === 'bronze'),
    silver: filteredBadges.filter(b => b.tier === 'silver'),
    gold: filteredBadges.filter(b => b.tier === 'gold')
  }

  return (
    <StudentShell>
      <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-3.5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Medal className="h-3 w-3 text-primary" />
                  <span>Achievements & Honors</span>
                </span>
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                Badges & Milestones
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Unlock badges as you attempt mock tests, answer questions, maintain active study streaks, and rank on the leaderboard.
              </p>
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-primary/10 border border-primary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-primary">{stats.earned_count}</span>
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider mt-0.5">Unlocked</span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-secondary/10 border border-secondary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{overallPct}%</span>
                <span className="text-[10px] font-bold text-secondary uppercase tracking-wider mt-0.5">Progress</span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{stats.total_count}</span>
                <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider mt-0.5">Total</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Pills & Progress Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="flex items-center p-1 bg-muted/50 rounded-2xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                activeFilter === 'all'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>All Badges</span>
              <span className={`text-[10px] ml-1.5 px-1.5 py-0.2 rounded-md font-extrabold ${activeFilter === 'all' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {badges.length}
              </span>
            </button>
            <button
              onClick={() => setActiveFilter('earned')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                activeFilter === 'earned'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>Unlocked</span>
              <span className={`text-[10px] ml-1.5 px-1.5 py-0.2 rounded-md font-extrabold ${activeFilter === 'earned' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {stats.earned_count}
              </span>
            </button>
            <button
              onClick={() => setActiveFilter('locked')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                activeFilter === 'locked'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>In Progress</span>
              <span className={`text-[10px] ml-1.5 px-1.5 py-0.2 rounded-md font-extrabold ${activeFilter === 'locked' ? 'bg-tertiary/15 text-tertiary' : 'bg-muted text-muted-foreground'}`}>
                {stats.total_count - stats.earned_count}
              </span>
            </button>
          </div>

          <div className="flex items-center gap-3 bg-card p-3 rounded-2xl border border-border/70 sm:min-w-[240px]">
            <div className="flex-1 space-y-1">
              <div className="flex justify-between text-[11px] font-bold text-muted-foreground">
                <span>Overall Completion</span>
                <span className="text-primary font-mono">{overallPct}%</span>
              </div>
              <Progress value={overallPct} className="h-2 rounded-full" />
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredBadges.length === 0 && (
          <div className="text-center py-16 px-4 rounded-2xl border border-dashed border-border/70 bg-card/40 backdrop-blur-glass">
            <Medal className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-foreground">No Badges Found</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              {activeFilter === 'earned'
                ? "You haven't unlocked any badges yet. Attempt tests, answer questions, and build streaks to earn them!"
                : activeFilter === 'locked'
                ? "All badges are already unlocked! Great work!"
                : "No badges are currently available."}
            </p>
          </div>
        )}

        {/* Tier Sections */}
        {(['bronze', 'silver', 'gold'] as const).map(tier => {
          const tierBadges = groupedByTier[tier]
          if (tierBadges.length === 0) return null

          const config = TIER_CONFIG[tier]
          const TierIcon = config.icon

          return (
            <div key={tier} className="space-y-3">
              <div className="flex items-center justify-between border-b border-border/40 pb-2.5">
                <div className="flex items-center gap-2">
                  <TierIcon className={`h-4.5 w-4.5 ${config.iconColor}`} />
                  <h2 className="text-sm sm:text-base font-extrabold text-foreground">{config.label}</h2>
                </div>
                <Badge variant="outline" className="text-[10px] font-mono font-bold uppercase rounded-lg border-border">
                  {tierBadges.length} {tierBadges.length === 1 ? 'Badge' : 'Badges'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
                {tierBadges.map(badge => {
                  const isEarned = badge.earned
                  const Icon = BADGE_ICON_MAP[badge.key] || Target

                  return (
                    <Card
                      key={badge.key}
                      className={`rounded-2xl border transition-all ${
                        isEarned
                          ? config.earnedBg
                          : 'border-border/60 bg-card/60 opacity-80 hover:opacity-100 shadow-glass-xs'
                      }`}
                    >
                      <CardContent className="p-3.5 sm:p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          {/* Badge Icon Container */}
                          <div
                            className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                              isEarned
                                ? 'bg-primary/10 text-primary border-primary/20 shadow-xs'
                                : 'bg-muted/80 border-border/40 text-muted-foreground'
                            }`}
                          >
                            {isEarned ? (
                              <Icon className="h-5.5 w-5.5 text-primary" />
                            ) : (
                              <Lock className="h-5 w-5 text-muted-foreground/60" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <p className="font-bold text-xs sm:text-sm text-foreground truncate">{badge.name}</p>
                              <Badge
                                variant="outline"
                                className={`text-[9px] font-extrabold uppercase tracking-wider rounded-md ${config.badgeClass}`}
                              >
                                {tier}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                              {badge.description}
                            </p>
                          </div>
                        </div>

                        {/* Status / Progress Footer */}
                        <div className="pt-2.5 border-t border-border/40">
                          {isEarned ? (
                            <div className="flex items-center justify-between text-xs font-bold text-primary">
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Unlocked
                              </span>
                              <span className="text-[10px] text-muted-foreground font-medium">{badge.awarded_at}</span>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-[11px] font-semibold text-muted-foreground">
                                <span>
                                  {badge.criteria?.type === 'accuracy_pct'
                                    ? `Accuracy: ${badge.current_value ?? 0}% / ${badge.threshold}%`
                                    : badge.criteria?.type === 'top_ranker'
                                    ? 'Goal: Top 10 Leaderboard Rank'
                                    : badge.criteria?.type === 'apex_achiever'
                                    ? 'Goal: Top 3 Leaderboard Rank'
                                    : `Progress: ${badge.current_value ?? 0} / ${badge.threshold}`}
                                </span>
                                <span className="font-mono">{badge.progress_pct ?? 0}%</span>
                              </div>
                              <Progress value={badge.progress_pct ?? 0} className="h-1.5 rounded-full" />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </StudentShell>
  )
}
